import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Draggable, { DraggableData, DraggableEvent, DraggableBounds } from 'react-draggable';
import { Box, Card, IconButton } from '@material-ui/core';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';

import { StoryCard, MapCard, GridCard, CardDb, updateCard } from '../redux/cardDbSlice';
import useOneCardDb from '../hooks/useOneCardDb';
import { CoordinateType } from '../general';
import { initial } from '../card_components/index';
import { CardMenuAction, ShowMenuType } from './BookEditor';

interface CardPadBodyProps {
  dragging: boolean;
  menuShowing: boolean;
  sheetKind?: string;
  resizing?: boolean;
  groupParent?: boolean;
  groupChild?: boolean;
  raised?: boolean;
}
const CardPadBody = styled.div<CardPadBodyProps>`
  position: absolute;
  background-color: #11ffee00;
  z-index: ${props => {
    if (props.dragging || props.raised) return 999;
    else if (props.menuShowing) return 998;
    else if (props.groupParent) return 0;
    else if (props.groupChild) return 0;
    else return 2;
  }};
  opacity: ${props => props.resizing ? 0 : 1};
  transition: ${props => (props.dragging || props.sheetKind !== 'grid') ? undefined : 'transform 300ms'};
`
interface CardContentCardProps {
  $width: number;
  $connectSelecting: boolean;
  $shadow: boolean;
}
const CardContentCard = styled(Card)<CardContentCardProps>`
  background-color: #11ffee00;
  width: ${props => props.$width + 'px'};
  ${props => props.$connectSelecting ? 'border: solid 2px blue;' : null};
  ${props => props.$shadow ? null : 'box-shadow: none'};
`
const LinkIconButton = styled(IconButton)`
  margin-left: 170px;
  margin-top: -48px;
  opacity: 0.8;
`

const isStoryCard = (card: CardDb): card is StoryCard => {
  return card.sheetKind === 'story';
}
const isMapCard = (card: CardDb): card is MapCard => {
  return card.sheetKind === 'map';
}
const isGridCard = (card: CardDb): card is GridCard => {
  return card.sheetKind === 'grid';
}

type CardPadContentsProps = {
  id: string;
  card: CardDb;
  cardMenuClicked: CardMenuAction;
  shadow: boolean;
  children: any;
  showMenu: ShowMenuType;
  handleMouseUp: (ev: React.MouseEvent<HTMLElement, MouseEvent>, id: string) => void;
  toggleShowMenu: (ev: React.MouseEvent<HTMLElement, MouseEvent>, id: string) => void;
  connectId?: string | undefined;
  raised?: boolean;
  overlapped?: boolean;
}
const CardPadContents: React.FC<CardPadContentsProps> = (props) => {
  const {id, card, cardMenuClicked, shadow, showMenu, handleMouseUp, toggleShowMenu, children, connectId, raised, overlapped} = props;
  const widthValue = card.currentWidth || card.defaultWidth;
  const width = card.orientation === 'vertical' ? initial.cardHeight : widthValue;

  return (
    <Box>
      <CardContentCard id={id}
        $width={width} $connectSelecting={id === connectId} $shadow={shadow}
        raised={raised || overlapped}
        aria-controls={showMenu?.id === id ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        className="card-handle"
        onMouseUp={(ev) => handleMouseUp(ev, id)}
        onContextMenu={(ev) => toggleShowMenu(ev, id)}
        onDoubleClick={() => cardMenuClicked.edit(id)}
      >
        { children }
      </CardContentCard>
      { (card.links && card.links.length >= 1) &&
        <LinkIconButton size="small" onClick={() => cardMenuClicked.jump(id)}>
          <LinkOutlinedIcon style={{color: '#2979ff'}} />
        </LinkIconButton>
      }
    </Box>
  );
}

type CardPadProps = {
  id: string;
  card?: CardDb;
  setCard?: (value: CardDb) => void;
  cardMenuClicked: CardMenuAction;
  showMenu: ShowMenuType;
  setShowMenu?: (value: ShowMenuType) => void;
  closeArrowMenu?: () => void;
  updateGroup?: (card: MapCard, data: DraggableData) => void;
  mode?: 'move' | 'connect';
  connectId?: string | undefined;
  handleChangeConnectId?: (id: string) => void;
  scaleCoefficient?: number;
  changeGrid?: (data: DraggableData) => void;
  resizing?: boolean;
  setDraggingId?: (id?: string) => void;
  raised?: boolean;
  overlapped?: boolean;
  updateOverlappedId?: (data: DraggableData) => void;
  getInternalPosition?: (id: string, data: DraggableData) => CoordinateType;
}
const CardPad: React.FC<CardPadProps> = (props) => {
  const {id, setCard, cardMenuClicked, showMenu, setShowMenu, closeArrowMenu, updateGroup, children, mode, connectId, handleChangeConnectId, scaleCoefficient, changeGrid, resizing, setDraggingId, raised, overlapped, updateOverlappedId, getInternalPosition} = props;
  const dispatch = useDispatch();
  const [dragging, setDragging] = useState<boolean>(false);
  const colomnWidth = 220;
  const rowHeight = 144;

  const cardValue = useOneCardDb(id);
  const card = props.card ? props.card : cardValue;
  if (!card) return <></>;

  const changeConnectIdExe = (ev: MouseEvent) => {
    if (ev.button !== 0) return;
    if (mode === 'connect' && handleChangeConnectId) handleChangeConnectId(id);
  }
  const dragCard = (data: DraggableData) => {
    setDragging(true);
    if (setDraggingId) setDraggingId(id);
    if (setShowMenu) setShowMenu(undefined);
    if (closeArrowMenu) closeArrowMenu();
    if (setCard && isMapCard(card) && getInternalPosition) {
      const position = getInternalPosition(id, data);
      setCard({ ...card, ...position });
    }
    if (updateOverlappedId) updateOverlappedId(data);
  }
  const dropCard = (ev: DraggableEvent, data: DraggableData) => {
    if (updateGroup && isMapCard(card)) updateGroup(card, data);
    else if (isGridCard(card)) dispatch(updateCard({id: id, x: data.x, y: data.y}));
  }
  const toggleShowMenu = (ev: React.MouseEvent<HTMLElement, MouseEvent>, id: string) => {
    if (!setShowMenu) return;
    if (showMenu?.id === id) setShowMenu(undefined);
    else if (isStoryCard(card) && card.parentId) setShowMenu({ id: id, card: card, anchorEl: ev.currentTarget, parentCardId: card.parentId });
    else setShowMenu({ id: id, card: card, anchorEl: ev.currentTarget });
  }
  const handleMouseUp = (ev: React.MouseEvent<HTMLElement, MouseEvent>, id: string) => {
    if (ev.button !== 0 || !setShowMenu) return;
    if ((isStoryCard(card) || mode === 'move') && !dragging) setShowMenu(undefined);
    else {
      setDragging(false);
      if (setDraggingId) setDraggingId();
    }
  }

  if (isMapCard(card)) {
    return (
      <Draggable position={{ x: card.x, y: card.y }} bounds={{ left: 0, top: 0 }}
        disabled={mode === 'connect' ? true : false}
        handle=".card-handle"
        onMouseDown={(ev) => changeConnectIdExe(ev)}
        onDrag={(ev: DraggableEvent, data: DraggableData) => dragCard(data)}
        onStop={(ev: DraggableEvent, data: DraggableData) => dropCard(ev, data)}
        scale={scaleCoefficient}
      >
        <CardPadBody dragging={dragging} menuShowing={showMenu?.id === id} resizing={resizing} groupParent={card.isGroup} groupChild={Boolean(card.groupParent)} raised={raised}>
          <CardPadContents id={id} card={card} cardMenuClicked={cardMenuClicked} shadow={card.shadow}
            children={children} showMenu={showMenu} handleMouseUp={handleMouseUp} toggleShowMenu={toggleShowMenu} connectId={connectId}
            raised={dragging || showMenu?.id === id} overlapped={overlapped}
          />
        </CardPadBody>
      </Draggable>
    );
  } else if (isGridCard(card)) {
    return (
      <Draggable position={{ x: card.column ? card.column * colomnWidth : 0, y: card.row ? card.row * rowHeight : 0 }}
        disabled={mode === 'connect' ? true : false}
        bounds={{ left: 0, top: 0 }}
        onMouseDown={(ev) => changeConnectIdExe(ev)}
        onDrag={(ev: DraggableEvent, data: DraggableData) => dragCard(data)}
        onStop={(ev: DraggableEvent, data: DraggableData) => changeGrid ? changeGrid(data) : undefined}
        scale={scaleCoefficient}
      >
        <CardPadBody dragging={dragging} menuShowing={showMenu?.id === id} sheetKind="grid">
          <CardPadContents id={id} card={card} cardMenuClicked={cardMenuClicked} shadow={card.shadow}
            children={children} showMenu={showMenu} handleMouseUp={handleMouseUp} toggleShowMenu={toggleShowMenu} connectId={connectId}
            raised={dragging || showMenu?.id === id}
          />
        </CardPadBody>
      </Draggable>
    );
  } else {
    return (
      <CardPadContents id={id} card={card} cardMenuClicked={cardMenuClicked} shadow={card.shadow}
        children={children} showMenu={showMenu} handleMouseUp={handleMouseUp} toggleShowMenu={toggleShowMenu}
        raised={showMenu?.id === id}
      />
    );
  }
}

export default CardPad;
